import { Link } from "react-router-dom";
import { ArrowRight, Quote, Star } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { CLIENT_NAMES, CASE_STUDIES, FACTORY_IMAGE } from "@/lib/site-data";

const SECTOR_BUCKETS = [
    { sector: "Automotive", clients: ["Bajaj Auto", "Mahindra", "Tata AutoComp", "Sona Comstar"], pct: 55 },
    { sector: "Auto Components", clients: ["Endurance", "Varroc", "Spicer", "Lumax", "Bharat Forge"], pct: 30 },
    { sector: "Engineering & Mfg", clients: ["Cummins", "Greaves Cotton", "Schaeffler", "ZF India"], pct: 15 },
];

const TESTIMONIALS = [
    {
        quote: "Vibhuti has been our extended logistics arm for nearly two decades. Their on-time discipline is what keeps our plants running.",
        name: "Senior Manager, Procurement",
        company: "Leading Automotive OEM",
    },
    {
        quote: "From dedicated warehousing to JIT line feeding — they've consistently scaled operations to match our growth.",
        name: "Plant Head",
        company: "Tier-1 Auto Ancillary",
    },
    {
        quote: "Owned infrastructure, transparent MIS, real WMS — exactly the partner manufacturing needs in India.",
        name: "Supply Chain Director",
        company: "Engineering Manufacturer",
    },
];

export default function Clients() {
    return (
        <div data-testid="page-clients">
            <PageHeader
                overline="Clients"
                title="Trusted by India's industry leaders."
                subtitle="45+ industrial clients across automotive, engineering and manufacturing have built their supply chains with Vibhuti."
                image={FACTORY_IMAGE}
                breadcrumb={[{ label: "Clients" }]}
                testId="clients-header"
            />

            {/* Logo wall */}
            <section className="bg-white py-20" data-testid="clients-logos">
                <div className="container-vl">
                    <p className="overline mb-4 text-center">Selected Clientele</p>
                    <h2 className="heading-h2 text-[#101A2C] text-center mb-12 max-w-3xl mx-auto">
                        Long-term partnerships, every one of them.
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
                        {CLIENT_NAMES.map((name, i) => (
                            <div
                                key={name}
                                data-testid={`client-${i}`}
                                className="bg-white p-6 h-24 flex items-center justify-center font-display uppercase tracking-wider text-sm text-[#28406A] hover:bg-[#28406A] hover:text-white transition-colors text-center"
                            >
                                {name}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Industry split */}
            <section className="bg-[#F8FAFC] py-20 border-y border-[#CBD5E1]" data-testid="clients-breakdown">
                <div className="container-vl grid grid-cols-12 gap-10">
                    <div className="col-span-12 lg:col-span-5">
                        <p className="overline mb-4">Industry Breakdown</p>
                        <h2 className="heading-h2 text-[#101A2C]">Concentrated where it matters most.</h2>
                        <p className="body-lg mt-5">
                            Our clientele is deliberately concentrated in industries where logistics is mission-critical — manufacturing, automotive and engineering.
                        </p>
                    </div>
                    <div className="col-span-12 lg:col-span-7 space-y-4">
                        {SECTOR_BUCKETS.map((b, i) => (
                            <div key={b.sector} className="bg-white border border-[#CBD5E1] p-6" data-testid={`sector-${i}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-display uppercase text-lg text-[#101A2C]">{b.sector}</h3>
                                    <span className="font-display text-2xl text-[#F4A000]">{b.pct}%</span>
                                </div>
                                <div className="h-2 bg-[#E9EEF3] mb-4">
                                    <div className="h-full bg-[#28406A]" style={{ width: `${b.pct}%` }} />
                                </div>
                                <p className="text-xs uppercase tracking-[0.2em] text-[#4B6390]">
                                    {b.clients.join(" · ")}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Case study */}
            <section className="bg-[#101A2C] text-white py-20 md:py-28 grain-overlay" data-testid="clients-casestudy">
                <div className="container-vl grid grid-cols-12 gap-10">
                    <div className="col-span-12 lg:col-span-5">
                        <p className="overline mb-4">Flagship Partnership</p>
                        <h2 className="heading-h2 text-white">{CASE_STUDIES[0].title}</h2>
                        <p className="text-sm uppercase tracking-[0.2em] text-[#F4A000] mt-4">{CASE_STUDIES[0].duration}</p>
                        <p className="mt-6 text-[#CBD5E1] leading-relaxed">{CASE_STUDIES[0].body}</p>
                    </div>
                    <div className="col-span-12 lg:col-span-7">
                        <div className="bg-[#28406A] p-8 border-l-4 border-[#F4A000]">
                            <p className="font-display uppercase text-[#F4A000] text-sm tracking-widest mb-5">Operational Footprint</p>
                            <ul className="space-y-4">
                                {CASE_STUDIES[0].highlights.map((h) => (
                                    <li key={h} className="flex items-start gap-3 text-sm md:text-base text-[#CBD5E1]">
                                        <span className="font-mono text-[#F4A000] text-xs mt-1">[ ▪ ]</span>
                                        {h}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="bg-white py-20 md:py-24" data-testid="clients-testimonials">
                <div className="container-vl">
                    <p className="overline mb-4">What customers say</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-12 max-w-3xl">In their words.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} className="bg-[#E9EEF3] p-7 border-t-4 border-[#F4A000]" data-testid={`testimonial-${i}`}>
                                <Quote className="h-7 w-7 text-[#28406A] mb-4" strokeWidth={1.5} />
                                <p className="text-sm md:text-base text-[#101A2C] leading-relaxed mb-6">"{t.quote}"</p>
                                <div className="flex items-center gap-2 text-[#F4A000] mb-3">
                                    {[0, 1, 2, 3, 4].map((s) => (
                                        <Star key={s} className="h-3.5 w-3.5 fill-current" strokeWidth={1.5} />
                                    ))}
                                </div>
                                <p className="text-xs uppercase tracking-[0.2em] text-[#4B6390] font-semibold">{t.name}</p>
                                <p className="text-xs text-[#4B6390]">{t.company}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#F4A000] py-16" data-testid="clients-cta">
                <div className="container-vl flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <h3 className="heading-h3 text-[#101A2C] max-w-2xl">Become the next industry leader on this list.</h3>
                    <Link to="/contact" data-testid="clients-cta-btn" className="btn-navy">
                        Become a Client <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
